<template>
  <div>
    <!--
      `total_documents` is a separate API aggregate, not a sum of the rows — a page carries only
      25 folders. Gated on `hasLoaded`, not just a non-zero total, since the store is a singleton
      and `meta` holds the PREVIOUS subject's numbers until the new response lands.
    -->
    <div
      v-if="!openFolder && hasLoaded && folders.meta.total"
      class="mb-2 font-weight-bold documents-total"
    >
      Total Documents: {{ totalDocuments }} (across {{ folders.meta.total }}
      {{ folders.meta.total === 1 ? "folder" : "folders" }})
    </div>

    <h1 v-if="heading && !openFolder">{{ heading }}</h1>

    <folder-table
      v-if="!openFolder"
      :key="subjectUserId"
      :subject-user-id="subjectUserId"
      :subject-kind="subjectKind"
      :can-create="canCreate"
      @open-folder="onOpenFolder"
      @share-folder="onShareFolder"
      @move-folder="onMoveFolder"
      @show-audience="onShowAudience"
    />

    <folder-contents
      v-else
      :key="openFolder.id"
      :folder="openFolder"
      :trail="trail"
      :can-create="canCreate"
      :subject-user-id="subjectUserId"
      @back="onBack"
      @open-folder="onOpenFolder"
      @share-folder="onShareFolder"
      @move-folder="onMoveFolder"
      @show-audience="onShowAudience"
      @share="onShareFromContents"
      @open-failed="onOpenFailed"
    />

    <move-folder-dialog
      v-model="showMove"
      :folder="moveTarget"
      :ancestors="trail"
      :subject-user-id="subjectUserId"
      @moved="onMoved"
    />

    <share-dialog
      v-model="showShare"
      :folder="shareTarget"
      :document-ids="shareDocumentIds"
      @shared="onShared"
    />

    <audience-dialog
      v-model="showAudience"
      :folder="audienceTarget"
      @manage-access="onManageAccess"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: "DocumentsArea",
  props: {
    subjectUserId: { type: Number, default: null },
    /**
     * `client` · `provider` · `self`. Selects the terminology used, and — in `canCreate` alone —
     * which old screen's "New folder" gate to reproduce. Every other control reads the API's
     * per-row `can` flags instead.
     */
    subjectKind: { type: String, default: "client" },
    heading: { type: String, default: "Documents" },
  },
  data() {
    return {
      trail: [],
      showMove: false,
      moveTarget: null,
      shareTarget: null,
      shareDocumentIds: [],
      audienceTarget: null,
      showShare: false,
      showAudience: false,
    };
  },
  computed: {
    /** The folder being shown — the last crumb, or null at the top level. */
    openFolder() {
      return this.trail.length ? this.trail[this.trail.length - 1] : null;
    },

    ...mapState({
      folders: (state) => state.folders.folders,
      hasLoaded: (state) => state.folders.hasLoaded,
    }),

    totalDocuments() {
      const meta = this.folders.meta || {};

      return typeof meta.total_documents === "number"
        ? meta.total_documents
        : "—";
    },

    /**
     * Whether "New folder" is offered — reproducing each old screen's gate EXACTLY, per subject.
     * No API flag exists for "create", so this is the one control still decided in the browser.
     */
    canCreate() {
      if (this.subjectKind === "client") {
        // patients/profile/documents/Folders.vue — no level check on the provider side.
        return (
          this.$isProvider() ||
          (this.$isAdmin() && this.$can("clients", "edit"))
        );
      }

      if (this.subjectKind === "provider") {
        // providers/profile/documents/Folders.vue
        return (
          (this.$isAdmin() && this.$can("providers", "edit")) ||
          (this.$isProvider() && this.$can("folder", "create"))
        );
      }

      // "self" — My Profile → Documents. `$isAdmin()` matches
      // admin/profile/documents/Folders.vue's `user.isAdmin || user.isManagingAdmin` exactly.
      return this.$isAdmin();
    },
  },
  watch: {
    subjectUserId: {
      immediate: true,
      handler(id) {
        if (!id) {
          return;
        }
        // A different subject is a different tab: drop the open folder and any carried-over
        // query. RESET_LISTS drops rows/counts too, or the header shows the last person's totals.
        this.trail = [];
        this.$store.commit("folders/RESET_QUERIES");
        this.$store.commit("folders/RESET_LISTS");

        this.showShare = false;
        this.shareTarget = null;
        this.shareDocumentIds = [];
        this.showAudience = false;
        this.audienceTarget = null;

        // Both lists, together — "Shared with me" shows its count on the tab pill.
        this.get(id);
        this.getSharedWithMe(id);
      },
    },
  },
  methods: {
    ...mapActions({
      get: "folders/get",
      getSharedWithMe: "folders/getSharedWithMe",
      getContents: "folders/getContents",
    }),

    onOpenFolder(folder) {
      this.trail = [...this.trail, folder];
    },

    /**
     * The crumb goes on before the contents are fetched, so the screen can render immediately.
     * If the fetch fails it has to come back off — otherwise every retry stacked another copy
     * of the same name on a trail the person never walked.
     */
    onOpenFailed() {
      this.trail = this.trail.slice(0, -1);
    },

    /**
     * `crumb` is the breadcrumb that was clicked, or null for "Documents". Going back to a folder
     * drops everything below it; going back to the top clears the trail.
     */
    onBack(crumb) {
      const index = crumb ? this.trail.findIndex((c) => c.id === crumb.id) : -1;
      this.trail = index < 0 ? [] : this.trail.slice(0, index + 1);

      if (!this.trail.length) {
        // Deleting or moving a document inside the folder can change both counts.
        this.get(this.subjectUserId);
        this.getSharedWithMe(this.subjectUserId);
      }
    },

    onMoved() {
      // The folder left this level, and its old and new parents both changed their counts.
      this.get(this.subjectUserId);
      if (this.openFolder) {
        this.getContents(this.openFolder.id);
      }
    },

    onMoveFolder(folder) {
      this.moveTarget = folder;
      this.showMove = true;
    },

    onShareFolder(folder) {
      this.shareTarget = folder;
      this.shareDocumentIds = [];
      this.showShare = true;
    },

    onShareFromContents({ folder, documentIds }) {
      this.shareTarget = folder;
      this.shareDocumentIds = documentIds;
      this.showShare = true;
    },

    onShowAudience(folder) {
      this.audienceTarget = folder;
      this.showAudience = true;
    },

    onManageAccess(folder) {
      this.showAudience = false;
      this.onShareFolder(folder);
    },

    onShared() {
      // The audience summary changes when a share lands; both tabs are refreshed since a loose
      // document's share affects the other tab's count too.
      this.get(this.subjectUserId);
      this.getSharedWithMe(this.subjectUserId);
      // Sharing a subfolder happens from inside its parent, and that list is the one on screen.
      if (this.openFolder) {
        this.getContents(this.openFolder.id);
      }
    },
  },
};
</script>
